import moment from 'moment'

/**
 * Handles snooze functionality for nudges
 * @param snoozeKey - The base key for localStorage (company ID will be appended)
 * @param companyId - The company ID
 * @param hoursToSnooze - Hours to snooze (default: 24)
 * @returns true if snoozed, false if not snoozed
 */
export function handleSnooze(snoozeKey: string, companyId: string, hoursToSnooze = 24): boolean {
  try {
    const fullKey = `${snoozeKey}-${companyId}`
    const snoozeUntil = localStorage.getItem(fullKey)

    // If no snooze exists, set one and return true (snoozed)
    if (!snoozeUntil) {
      localStorage.setItem(fullKey, moment().add(hoursToSnooze, 'hours').toISOString())
      return true
    }

    // If snooze exists and we're still in snooze period, return true (snoozed)
    if (moment().isBefore(moment(snoozeUntil))) {
      return true
    }

    // Snooze has expired, return false (not snoozed)
    return false
  } catch (error) {
    console.error('Error in handleSnooze:', error)
    return false
  }
}

/**
 * Checks if currently in a snooze period without setting a new snooze
 * @param snoozeKey - The base key for localStorage (company ID will be appended)
 * @param companyId - The company ID
 * @returns true if currently snoozed, false if not
 */
export function isCurrentlySnoozed(snoozeKey: string, companyId: string): boolean {
  try {
    const fullKey = `${snoozeKey}-${companyId}`
    const snoozeUntil = localStorage.getItem(fullKey)

    if (!snoozeUntil) {
      return false
    }

    return moment().isBefore(moment(snoozeUntil))
  } catch (error) {
    console.error('Error in isCurrentlySnoozed:', error)
    return false
  }
}

/**
 * Sets a snooze without checking existing state
 * @param snoozeKey - The base key for localStorage (company ID will be appended)
 * @param companyId - The company ID
 * @param hoursToSnooze - Hours to snooze (default: 24)
 */
export function setSnooze(snoozeKey: string, companyId: string, hoursToSnooze = 24): void {
  try {
    const fullKey = `${snoozeKey}-${companyId}`
    localStorage.setItem(fullKey, moment().add(hoursToSnooze, 'hours').toISOString())
  } catch (error) {
    console.error('Error in setSnooze:', error)
  }
}

/**
 * Clears/removes a snooze
 * @param snoozeKey - The base key for localStorage (company ID will be appended)
 * @param companyId - The company ID
 */
export function clearSnooze(snoozeKey: string, companyId: string): void {
  try {
    const fullKey = `${snoozeKey}-${companyId}`
    localStorage.removeItem(fullKey)
  } catch (error) {
    console.error('Error in clearSnooze:', error)
  }
}
